import {FC, useEffect, useState} from 'react'
import {CustomModal} from '_metronic/partials/modals/CustomModal'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Input from 'app/components/Input'
import {toAbsoluteUrl} from '_metronic/helpers'
import {useAuth} from 'app/modules/auth'
import {deleteObject, getDownloadURL, ref, uploadBytes} from 'firebase/storage'
import db, {storage} from 'services/firebase'
import Dropzone from 'react-dropzone'
import {addDoc, collection, doc, serverTimestamp, setDoc, updateDoc} from '@firebase/firestore'
import {DocumentDataType} from '../Documents'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  isEditMode: boolean
  document?: DocumentDataType
}

const accessArr = [
  {title: 'Restricted', iconPath: '/media/icon/lock.svg'},
  {title: 'Public', iconPath: '/media/icon/world.svg'},
]

const SUPPORTED_FILE_FORMATS = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

const initialValues = {
  id: '',
  title: '',
  documentName: '',
  documentURL: '',
  access: 'Restricted',
}

const validationSchema = Yup.object({
  title: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(200, 'Maximum 200 characters')
    .required('Name is required'),
  documentURL: Yup.mixed<File | string>()
    .required('Document is required')
    .test('fileFormat', 'Unsupported format for document', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && SUPPORTED_FILE_FORMATS.includes(value.type)
      }
      return false
    })
    .test('fileSize', 'Document size is too large', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && value.size <= 5 * 1024 * 1024
      }
      return false
    }),
  access: Yup.string()
    .oneOf(['Restricted', 'Public'], 'Access must be either restricted or public')
    .required('Access is required'),
})

const AddEditDocumentModal: FC<Props> = ({isModalOpen, hideModal, isEditMode, document}) => {
  const {currentUser} = useAuth()

  const [loading, setLoading] = useState(false)

  const [uploadedFile, setUploadedFile] = useState<File>()

  const [isDropzoneShownForEditMode, setIsDropzoneShownForEditMode] = useState(false)

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const formik = useFormik<DocumentDataType>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const documentsRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'documents',
          'all_documents'
        )

        if (!isEditMode) {
          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.documentURL instanceof File) {
            fileName = values.documentURL.name
            fileId = `${Date.now()}`

            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/documents/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.documentURL)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          const newDocRef = await addDoc(documentsRef, {
            title: values.title,
            documentName: downloadURL.length > 0 ? fileName : values.documentName,
            documentURL: downloadURL.length > 0 ? downloadURL : values.documentURL,
            documentId: downloadURL.length > 0 ? fileId : '',
            access: values.access,
            isCustomDocumentUploaded: downloadURL.length > 0 ? true : false,
          })

          await updateDoc(newDocRef, {
            id: newDocRef.id,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')

          await addDoc(activitiesRef, {
            message: `${values.title} document for trust center has been added`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Document added',
          })

          showSuccessToast({message: 'New document has been added successfully'})
        } else {
          const docRef = doc(documentsRef, values.id)
          let isOldFileDeleted = false

          if (document?.isCustomDocumentUploaded && document?.documentURL !== values.documentURL) {
            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/documents/${document?.documentId}-${document?.documentName}`
            )

            await deleteObject(fileRef)

            isOldFileDeleted = true
          }

          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.documentURL instanceof File) {
            fileName = values.documentURL.name
            fileId = `${Date.now()}`

            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/documents/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.documentURL!)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          await setDoc(docRef, {
            id: docRef.id,
            title: values.title,
            documentName: downloadURL.length > 0 ? fileName : values.documentName,
            documentURL: downloadURL.length > 0 ? downloadURL : values.documentURL,
            documentId: downloadURL.length > 0 ? fileId : isOldFileDeleted ? '' : values.documentId,
            access: values.access,
            isCustomDocumentUploaded:
              downloadURL.length > 0
                ? true
                : isOldFileDeleted
                ? false
                : values?.isCustomDocumentUploaded,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${document?.title} document for trust center has been updated`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Document updated',
          })

          showSuccessToast({message: 'Document has been updated successfully'})
        }
      } catch (error) {
        console.log('Error while updating document', error)
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  useEffect(() => {
    if (document?.title) {
      formik.setValues({
        id: document!.id,
        title: document!.title,
        documentName: document.documentName,
        documentURL: document!.documentURL as string,
        documentId: document!.documentId,
        access: document!.access,
        isCustomDocumentUploaded: document.isCustomDocumentUploaded ?? false,
      })
    }
  }, [document])

  const okBtnClickHandler = () => {
    formik.submitForm()
  }

  const handleRemoveFile = () => {
    setUploadedFile(undefined)
    formik.setFieldValue('documentURL', null)
    formik.setFieldValue('documentName', '')
    setIsDropzoneShownForEditMode(true)
  }

  const handleUpload = (acceptedFile: File[]) => {
    setUploadedFile(acceptedFile[0])
    formik.setFieldTouched('documentURL', true)
    formik.setFieldValue('documentURL', acceptedFile[0])
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={`${isEditMode ? 'Edit Document' : 'Add Document'}`}
      okBtnText={`${isEditMode ? 'Save' : 'Add Document'}`}
      okBtnClickHandler={okBtnClickHandler}
      isCloseIconShown={true}
      isLoading={loading}
      isLoadingBtnText={`${isEditMode ? 'Saving...' : 'Adding Document...'}`}
    >
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{gap: '20px'}}
      >
        <Input
          type='text'
          label='Document Name'
          placeholder='Document Name'
          error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
          {...formik.getFieldProps('title')}
        />

        <div className='w-100 d-flex flex-column' style={{gap: '8px'}}>
          <label className='fw-medium text-dark fs-6'>Document</label>

          {(isEditMode && document?.isCustomDocumentUploaded && !isDropzoneShownForEditMode) ||
          uploadedFile ? (
            <div
              className=''
              style={{border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px'}}
            >
              <div className='d-flex align-items-center' style={{gap: '16px'}}>
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{
                    backgroundColor: '#F3F4F6',
                    borderRadius: '12px',
                    height: '48px',
                    width: '48px',
                  }}
                >
                  <img
                    alt='file-text-icon'
                    src={toAbsoluteUrl('/media/icon/file-text.svg')}
                    className=''
                    style={{height: '32px', width: '32px'}}
                  />
                </div>

                <span className='flex-fill text-dark fs-6 fw-medium'>
                  {uploadedFile?.name ?? document?.documentName}
                </span>

                <Button
                  style={{
                    width: '34px',
                    height: '34px',
                  }}
                  onClick={handleRemoveFile}
                >
                  <img
                    alt='trash-icon'
                    src={toAbsoluteUrl('/media/icon/trash.svg')}
                    className=''
                    style={{height: '18px', width: '18px'}}
                  />
                </Button>
              </div>
            </div>
          ) : (
            <Dropzone
              onDrop={handleUpload}
              minSize={1024}
              accept={{
                'application/pdf': ['.pdf'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                  '.docx',
                ],
              }}
              maxSize={5 * 1024 * 1024}
            >
              {({getRootProps, getInputProps}) => (
                <div
                  {...getRootProps({
                    className: 'dropzone',
                    style: {
                      backgroundColor: 'white',
                      border: '2px dashed #E5E7EB',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px 32px',
                      textAlign: 'center',
                      cursor: 'pointer',
                    },
                  })}
                >
                  <input {...getInputProps()} />

                  <div className='d-flex flex-column align-items-center' style={{gap: '16px'}}>
                    <div className='d-flex flex-column align-items-center' style={{gap: '7px'}}>
                      <div
                        className='d-flex align-items-center justify-content-center'
                        style={{
                          backgroundColor: '#F3F4F6',
                          borderRadius: '12px',
                          height: '48px',
                          width: '48px',
                        }}
                      >
                        <img
                          alt='document-icon'
                          src={toAbsoluteUrl('/media/icon/file-text.svg')}
                          className=''
                          style={{height: '32px', width: '32px'}}
                        />
                      </div>

                      <span className='text-dark fs-6 fw-medium'>Drag and drop file here</span>

                      <span className='text-secondary-gray' style={{fontSize: '13px'}}>
                        Supported files: pdf, doc, docx. Max 5MB.
                      </span>
                    </div>

                    <div>
                      <Button
                      >
                        <img
                          alt='upload-icon'
                          src={toAbsoluteUrl('/media/icon/upload.svg')}
                          className=''
                          style={{height: '18px', width: '18px'}}
                        />
                        <span className='text-dark fs-6 fw-medium'>Upload File</span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Dropzone>
          )}

          {formik.touched.documentURL && formik.errors.documentURL && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.documentURL}</span>
              </div>
            </div>
          )}
        </div>

        <div className='w-100 d-flex flex-column justify-content-center' style={{gap: '8px'}}>
          <label className='fw-medium text-dark fs-6'>Access</label>

          <div role='radio' className='d-flex align-items-center' style={{gap: '12px'}}>
            {accessArr.map((access, idx) => (
              <div
                key={idx}
                className='flex-fill cursor-pointer'
                style={{
                  backgroundColor: `${formik.values.access === access.title ? '#F9FAFB' : 'white'}`,
                  border: `${
                    formik.values.access === access.title ? '2px solid black' : '2px solid #E5E5E5'
                  }`,
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
                onClick={() => {
                  formik.setFieldValue('access', access.title)
                }}
              >
                <div
                  className='d-flex justify-content-center align-items-center'
                  style={{gap: '6px'}}
                >
                  <img
                    alt={'icon'}
                    src={toAbsoluteUrl(access.iconPath)}
                    className=''
                    style={{height: '16px', width: '16px'}}
                  />
                  <span className='text-dark fs-6'>{access.title}</span>
                </div>
              </div>
            ))}
          </div>

          {formik.touched.access && formik.errors.access && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.access}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  )
}

export default AddEditDocumentModal
